import { useUser, useClerk, SignInButton } from '@clerk/clerk-react';
import { useState, useRef, useEffect } from 'react';
import { LogOut, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import posthog from 'posthog-js';
import { Analytics } from '@/lib/analytics';

interface AuthButtonProps {
  isMobile?: boolean;
}

export default function AuthButton({ isMobile = false }: AuthButtonProps) {
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { openUserProfile } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Handle clicking outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);
  
  const size = isMobile ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  const buttonSize = isMobile ? "text-sm px-3 py-1.5" : "text-base px-4 py-2";
  
  // Loading state
  if (!isLoaded) {
    return (
      <div className={`${size} rounded-full bg-muted animate-pulse`} />
    );
  }
  
  // Not signed in - show sign in button
  if (!isSignedIn) {
    return (
      <SignInButton mode="modal">
        <button 
          className={`font-emphasis font-semibold uppercase-tracked
                     bg-sport-accent text-white rounded-md
                     transition-all duration-200 hover:transform hover:-translate-y-0.5
                     hover:shadow-md ${buttonSize}`}
          onClick={() => {
            Analytics.trackSignInAttempted({
              source: 'header'
            });
          }}
        >
          Sign In
        </button>
      </SignInButton>
    );
  }
  
  // Signed in - show user button with custom menu
  const userInitials = user.firstName && user.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user.firstName
    ? user.firstName.slice(0, 2).toUpperCase()
    : user.primaryEmailAddress?.emailAddress.slice(0, 2).toUpperCase() || 'U';
    
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={`${size} rounded-full bg-sport-accent text-white font-semibold
                    flex items-center justify-center cursor-pointer
                    transition-transform duration-200 hover:scale-105
                    border-2 border-transparent hover:border-sport-accent/20`}
        aria-label="User menu"
      >
        {user.imageUrl ? (
          <img 
            src={user.imageUrl} 
            alt={user.fullName || 'User avatar'} 
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          userInitials
        )}
      </button>
      
      {/* Dropdown Menu */}
      {showMenu && (
        <div className={`absolute ${isMobile ? 'right-0' : 'right-0'} mt-2 
                        bg-surface rounded-lg shadow-lg border border-border
                        overflow-hidden z-50 min-w-[200px]
                        animate-in fade-in slide-in-from-top-1 duration-200`}>
          {/* User Info */}
          <div className="px-4 py-3 border-b border-border">
            <p className="font-emphasis text-sm font-medium text-foreground">
              {user.fullName || user.firstName || 'User'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {user.primaryEmailAddress?.emailAddress}
            </p>
          </div>
          
          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setShowMenu(false);
                openUserProfile();
              }}
              className="w-full px-4 py-2 text-left text-sm text-foreground
                       hover:bg-surface-alt transition-colors duration-150
                       flex items-center gap-2"
            >
              <User className="w-4 h-4" />
              Profile & Settings
            </button>
            
            <hr className="my-1 border-border" />
            
            <button
              onClick={() => {
                setShowMenu(false);
                // Track sign out event before signing out
                posthog.capture('sign_out', {
                  timestamp: new Date().toISOString(),
                });
                signOut();
              }}
              className="w-full px-4 py-2 text-left text-sm text-foreground
                       hover:bg-surface-alt transition-colors duration-150
                       flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}